import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Users, Zap, MessageSquare, Calendar, RefreshCw, Loader2, 
  FolderOpen, Briefcase, ArrowRightLeft, TrendingUp, Clock,
  CheckCircle, XCircle, Shield, UserCheck, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PilotDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/pilot/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load PILOT dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (userId, email) => {
    if (!window.confirm(`Deactivate PILOT access for ${email}?`)) return;
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/admin/pilot/deactivate/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`PILOT access deactivated for ${email}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to deactivate');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!data) return null;

  const { overview, engagement, users } = data;
  const activePilots = users.filter(u => u.pilot_status === 'active');
  const expiredPilots = users.filter(u => u.pilot_status === 'expired');

  return (
    <div className="space-y-6" data-testid="pilot-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">PILOT Program Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">Monitor tester engagement and conversion metrics</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-pilot-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="pilot-overview-cards">
        <StatCard
          icon={Users} label="Total Pilots" value={overview.total_pilot_users}
          color="purple" testId="stat-total-pilots"
        />
        <StatCard
          icon={CheckCircle} label="Active" value={overview.active_pilots}
          color="green" testId="stat-active-pilots"
        />
        <StatCard
          icon={Clock} label="Expired" value={overview.expired_pilots}
          color="gray" testId="stat-expired-pilots"
        />
        <StatCard
          icon={UserCheck} label="Converted to Paid" value={overview.converted_to_paid}
          color="blue" testId="stat-converted"
        />
        <StatCard
          icon={TrendingUp} label="Conversion Rate" value={`${overview.conversion_rate}%`}
          color="emerald" testId="stat-conversion-rate"
        />
      </div>

      {/* Engagement Metrics */}
      <Card data-testid="pilot-engagement-section">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Aggregate Engagement</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            <MetricTile icon={FolderOpen} label="Resources Created" value={engagement.total_resources_created} />
            <MetricTile icon={Zap} label="Resources Activated" value={engagement.total_resources_activated} />
            <MetricTile icon={Briefcase} label="Projects Created" value={engagement.total_projects_created} />
            <MetricTile icon={Zap} label="Projects Activated" value={engagement.total_projects_activated} />
            <MetricTile icon={ArrowRightLeft} label="Matches Initiated" value={engagement.total_matches_initiated} />
            <MetricTile icon={MessageSquare} label="Messages Sent" value={engagement.total_messages_sent} />
            <MetricTile icon={Calendar} label="Interviews" value={engagement.total_interviews_scheduled} />
          </div>
        </CardContent>
      </Card>

      {/* Active Pilots Table */}
      {activePilots.length > 0 && (
        <Card data-testid="active-pilots-section">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
              <CardTitle className="text-lg">Active Pilots ({activePilots.length})</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <UserTable
              users={activePilots}
              expandedUser={expandedUser}
              setExpandedUser={setExpandedUser}
              onDeactivate={handleDeactivate}
              showDeactivate
            />
          </CardContent>
        </Card>
      )}

      {/* Expired Pilots Table */}
      {expiredPilots.length > 0 && (
        <Card data-testid="expired-pilots-section">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-gray-400" />
              <CardTitle className="text-lg">Expired Pilots ({expiredPilots.length})</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <UserTable
              users={expiredPilots}
              expandedUser={expandedUser}
              setExpandedUser={setExpandedUser}
            />
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {users.length === 0 && (
        <Card>
          <CardContent className="py-16 text-center">
            <Shield className="w-12 h-12 text-purple-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700">No PILOT users yet</h3>
            <p className="text-sm text-gray-500 mt-1">
              Users will appear here when they subscribe using the PILOT promo code at checkout.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color, testId }) => {
  const colorMap = {
    purple: 'bg-purple-50 text-purple-600',
    green: 'bg-green-50 text-green-600',
    gray: 'bg-gray-50 text-gray-600',
    blue: 'bg-blue-50 text-blue-600',
    emerald: 'bg-emerald-50 text-emerald-600'
  };
  return (
    <Card data-testid={testId}>
      <CardContent className="p-4">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${colorMap[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-xs text-gray-500 mt-0.5">{label}</p>
      </CardContent>
    </Card>
  );
};

const MetricTile = ({ icon: Icon, label, value }) => (
  <div className="text-center p-3 bg-gray-50 rounded-lg">
    <Icon className="w-4 h-4 text-gray-400 mx-auto mb-1" />
    <p className="text-xl font-bold text-gray-900">{value}</p>
    <p className="text-[11px] text-gray-500 leading-tight">{label}</p>
  </div>
);

const UserTable = ({ users, expandedUser, setExpandedUser, onDeactivate, showDeactivate }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b text-left text-gray-500 text-xs uppercase tracking-wide">
          <th className="pb-2 pr-4">User</th>
          <th className="pb-2 pr-4">Role</th>
          <th className="pb-2 pr-4">Plan</th>
          <th className="pb-2 pr-4">Activated</th>
          <th className="pb-2 pr-4">{showDeactivate ? 'Days Left' : 'Expired'}</th>
          <th className="pb-2 pr-4">Engagement</th>
          <th className="pb-2"></th>
        </tr>
      </thead>
      <tbody>
        {users.map(user => {
          const m = user.metrics;
          const totalActions = m.resources_created + m.projects_created + m.matches_initiated + m.messages_sent + m.interviews_scheduled;
          const isExpanded = expandedUser === user.id;
          
          return (
            <React.Fragment key={user.id}>
              <tr
                className="border-b last:border-0 hover:bg-gray-50 cursor-pointer"
                onClick={() => setExpandedUser(isExpanded ? null : user.id)}
                data-testid={`pilot-user-row-${user.email}`}
              >
                <td className="py-3 pr-4">
                  <div>
                    <p className="font-medium text-gray-900">{user.first_name} {user.last_name}</p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                    {user.company_name && <p className="text-xs text-gray-400">{user.company_name}</p>}
                  </div>
                </td>
                <td className="py-3 pr-4">
                  <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700 capitalize">
                    {user.role?.replace('_', ' ')}
                  </span>
                </td>
                <td className="py-3 pr-4">
                  <span className="px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-700 capitalize">
                    {user.subscription_tier}
                  </span>
                </td>
                <td className="py-3 pr-4 text-gray-600">
                  {user.pilot_activated_at ? new Date(user.pilot_activated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                </td>
                <td className="py-3 pr-4">
                  {showDeactivate ? (
                    <span className={`font-medium ${user.days_remaining <= 3 ? 'text-red-600' : user.days_remaining <= 7 ? 'text-orange-600' : 'text-green-600'}`}>
                      {user.days_remaining}d
                    </span>
                  ) : (
                    <span className="text-gray-500">
                      {user.pilot_expires_at ? new Date(user.pilot_expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                    </span>
                  )}
                </td>
                <td className="py-3 pr-4">
                  <EngagementBar total={totalActions} />
                </td>
                <td className="py-3 text-right">
                  <div className="flex items-center gap-1 justify-end">
                    {showDeactivate && (
                      <Button
                        variant="ghost" size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                        onClick={(e) => { e.stopPropagation(); onDeactivate(user.id, user.email); }}
                        data-testid={`deactivate-pilot-${user.email}`}
                      >
                        <XCircle className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </div>
                </td>
              </tr>
              {isExpanded && (
                <tr>
                  <td colSpan="7" className="bg-gray-50 px-4 py-3">
                    <div className="grid grid-cols-3 sm:grid-cols-7 gap-3">
                      <MiniMetric label="Resources" value={m.resources_created} sub={`${m.resources_activated} active`} />
                      <MiniMetric label="Projects" value={m.projects_created} sub={`${m.projects_activated} active`} />
                      <MiniMetric label="Matches" value={m.matches_initiated} />
                      <MiniMetric label="Messages" value={m.messages_sent} />
                      <MiniMetric label="Interviews" value={m.interviews_scheduled} />
                      <MiniMetric label="Last Login" value={user.last_login ? new Date(user.last_login).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'} />
                      <MiniMetric label="Total Actions" value={totalActions} highlight />
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  </div>
);

const EngagementBar = ({ total }) => {
  const level = total === 0 ? 'none' : total < 3 ? 'low' : total < 10 ? 'medium' : 'high';
  const colorMap = { none: 'bg-gray-200', low: 'bg-orange-400', medium: 'bg-blue-500', high: 'bg-green-500' };
  const labelMap = { none: 'No activity', low: 'Low', medium: 'Medium', high: 'High' };
  
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${colorMap[level]}`} style={{ width: `${Math.min(100, total * 10)}%` }} />
      </div>
      <span className="text-xs text-gray-500">{labelMap[level]}</span>
    </div>
  );
};

const MiniMetric = ({ label, value, sub, highlight }) => (
  <div className={`text-center p-2 rounded ${highlight ? 'bg-purple-100' : 'bg-white'}`}>
    <p className={`text-lg font-bold ${highlight ? 'text-purple-700' : 'text-gray-900'}`}>{value}</p>
    <p className="text-[10px] text-gray-500">{label}</p>
    {sub && <p className="text-[10px] text-gray-400">{sub}</p>}
  </div>
);

export default PilotDashboardPage;
